import useForm, { FormInstance } from 'antd/lib/form/hooks/useForm';
import Modal from 'antd/lib/modal/Modal';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    FieldType,
    formValidAndRequiredFieldsNotEmpty,
    FormFieldData,
    integrationModalContentUsingFormInstance,
} from '../helpers/formHelpers';
import {
    displayCapabilityForIntegrationType,
    displayTextForProviderType,
    providerTypeForIntegrationType,
} from '../helpers/integrationHelpers';
import { RootState } from '../reducers/reducers';
import { AppEntity, IntegrationProvider, IntegrationProviderType, IntegrationType } from '../types';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { displayTextForAppPlatform } from '../helpers/displayHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { selectApp, selectOrgProviders } from '../selectors/selectors';
import { ConnectNewProviderButtonForIntegrationType } from './ConnectButtonForIntegrationType';
import { AddIntegrationModalProps } from './setup/useAddIntegrationModal';
import { connectDataForIntegrationType } from './ConnectIntegrationModal';

interface ProvidersAndApps {
    provider: IntegrationProvider;
    appsUsingProvider: AppEntity[];
}

interface IntegrationProviderModalProps extends AddIntegrationModalProps {
    onConnectNewProvider: (integrationType: IntegrationType) => void;
}

const IntegrationProviderModal: React.FC<IntegrationProviderModalProps> = (props: IntegrationProviderModalProps) => {
    const providersAndApps: ProvidersAndApps[] = useSelector((state: RootState) => {
        if (props.integrationType) {
            const integrationProviders = selectOrgProviders(state, props.orgId) ?? [];

            const providersMatchingIntegrationType = integrationProviders.filter(
                (provider) => provider.type === providerTypeForIntegrationType(props.integrationType),
            );

            return providersMatchingIntegrationType.map((provider) => {
                const appsUsingProvider = provider.appIds.map((appId) => {
                    return selectApp(state, appId);
                });

                return {
                    provider,
                    appsUsingProvider,
                };
            });
        }

        return [];
    });

    const firstProviderId = providersAndApps.length === 0 ? null : providersAndApps[0].provider.id;

    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [form] = useForm();
    const [formIsValid, setFormIsValid] = React.useState(true);

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const valid = formValidAndRequiredFieldsNotEmpty(form, ['providerId']);

        setFormIsValid(valid);
    };

    const onFinish = (_, __, form: FormInstance) => {
        form.validateFields()
            .then((values) => {
                const data = {
                    ...values,
                };

                props.addIntegration(props.integrationType, data);
            })
            .catch(() => setFormIsValid(false));
    };

    const fields =
        props.integrationType ?
            providerFormFields(
                providersAndApps,
                providerTypeForIntegrationType(props.integrationType),
                props.integrationType,
            )
        :   [];
    const providerDisplayText =
        props.integrationType ? displayTextForProviderType(providerTypeForIntegrationType(props.integrationType)) : '';
    const title =
        props.integrationType ?
            `Select ${providerDisplayText} installation for ${displayCapabilityForIntegrationType(props.integrationType)}`
        :   'Select installation';

    React.useEffect(() => {
        if (props.open) {
            // reset initial value
            form.setFields([{ name: 'providerId', value: firstProviderId }]);
        }
    }, [props.open]);

    const onClickInstallRunwayIntegration = (integrationType: IntegrationType) => {
        props.addIntegration(integrationType, {});
    };

    return (
        <Modal
            title={title}
            onCancel={props.onCancel}
            width={600}
            confirmLoading={props.isLoading}
            open={props.open}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={props.onCancel}>Cancel</Button>
                    <ConnectNewProviderButtonForIntegrationType
                        cta="New installation"
                        integrationType={props.integrationType}
                        orgId={props.orgId}
                        orgIdParam={orgIdParam}
                        appIdParam={appIdParam}
                        disabled={false}
                        onClickShowInstallationModal={props.onConnectNewProvider}
                        onClickInstallRunwayIntegration={onClickInstallRunwayIntegration}
                    />
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                        onClick={() => form.submit()}
                        type="primary"
                        loading={props.isLoading}
                        disabled={!formIsValid}
                    >
                        Connect
                    </PermissionedActionButton>
                </Space>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignContent: 'center' }}>
                <Text type="secondary">It looks like you&apos;ve already authorized {providerDisplayText}.</Text>
                <div>
                    {props.integrationType && (
                        <div>
                            {integrationModalContentUsingFormInstance(
                                form,
                                'vertical',
                                props.integrationType,
                                null,
                                fields,
                                onFieldsChange,
                                onFinish,
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default IntegrationProviderModal;

const providerFormFields: (
    providersAndApps: ProvidersAndApps[],
    providerType: IntegrationProviderType,
    integrationType: IntegrationType,
) => FormFieldData[] = (providersAndApps, providerType, integrationType) => {
    if (providersAndApps.length === 0) {
        return [];
    }

    // special helper text for Google case
    let helpMessage = null;
    if (providerType == IntegrationProviderType.Google) {
        helpMessage =
            'Note: in order to reuse an existing Google installation, its API Key and corresponding service account must have been provisioned and scoped appropriately for this integration as well';
    }

    const reusedFields = reusedProviderFormFieldsForIntegrationType(integrationType);

    return [
        {
            label: 'Select existing installation',
            name: 'providerId',
            fieldType: FieldType.Select,
            data: providersAndApps.map((providerAndApps) => {
                const appsDisplay = providerAndApps.appsUsingProvider.map((app) => {
                    const integrationTypesForApp = providerAndApps.provider.installedIntegrationTypesByAppId[app.id];
                    const integrationsUsingProvider = integrationTypesForApp.map((integrationType) =>
                        displayCapabilityForIntegrationType(integrationType),
                    );

                    return `${app.appSettings.appName} ${displayTextForAppPlatform(
                        app.appSettings.platform,
                        app.appSettings.appStoreType,
                    )} (${integrationsUsingProvider.join(', ')})`;
                });
                const displayText = `${providerAndApps.provider.name} - in use by ${appsDisplay.join(', ')}`;

                return {
                    value: providerAndApps.provider.id,
                    displayValue: displayText,
                };
            }),
            rules: [{ required: true }],
            placeholder: 'Select',
            helpMessage: helpMessage,
        },
        ...reusedFields,
    ];
};

// If reusing an existing provider, add any additional fields that are required here
const reusedProviderFormFieldsForIntegrationType: (integrationType: IntegrationType) => FormFieldData[] = (
    integrationType,
) => {
    switch (integrationType) {
        case IntegrationType.GoogleAnalytics:
            return connectDataForIntegrationType(integrationType);
        default:
            return [];
    }
};
